// frontend/src/services/dataManager.js
import { fetchExtendedMarketData, fetchMarketDataRange } from './api';

const DEBUG = process.env.REACT_APP_DEBUG === 'true';

class DataManager {
  constructor() {
    this.cache = new Map();
    this.loadingStates = new Map();
    this.maxCacheSize = 20; // Increase from 10 to cache more symbols
    this.maxDataPoints = 10000; // Increase from 5000 to 10000
  }

  // Generate cache key
  getCacheKey(symbol, interval) {
    return `${symbol}_${interval}`;
  }

  // Get cached data
  getCachedData(symbol, interval) {
    const key = this.getCacheKey(symbol, interval);
    const cached = this.cache.get(key);
    
    if (cached && this.isDataFresh(cached.timestamp)) {
      return cached.data;
    }
    
    return null;
  }

  // Check if data is fresh (less than 5 minutes old for active intervals)
  isDataFresh(timestamp, maxAge = 5 * 60 * 1000) {
    return Date.now() - timestamp < maxAge;
  }

  // Set cached data
  setCachedData(symbol, interval, data) {
    const key = this.getCacheKey(symbol, interval);
    
    // Implement LRU cache - remove oldest entries if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  // Load extended historical data
  async loadExtendedData(symbol, interval = '1d', options = {}) {
    const key = this.getCacheKey(symbol, interval);
    
    // Check if already loading
    if (this.loadingStates.get(key)) {
      return this.loadingStates.get(key);
    }

    // Check cache first
    const cached = this.getCachedData(symbol, interval);
    if (cached && !options.forceReload) {
      return cached;
    }

    // Create loading promise
    const loadingPromise = this.fetchExtendedHistoricalData(symbol, interval, options);
    this.loadingStates.set(key, loadingPromise);

    try {
      const data = await loadingPromise;
      this.setCachedData(symbol, interval, data);
      return data;
    } finally {
      this.loadingStates.delete(key);
    }
  }

  // Fetch extended historical data with progressive loading
  async fetchExtendedHistoricalData(symbol, interval, options = {}) {
    const maxDataPoints = Number.isFinite(options.maxDataPoints)
      ? options.maxDataPoints
      : 10000; // Increased default
    
    try {
      // Try to get extended data from the API
      const extendedData = await fetchExtendedMarketData(symbol, interval, maxDataPoints);
      
      if (extendedData && extendedData.candles && extendedData.candles.length > 0) {
        if (DEBUG) {
          console.log(`✅ Loaded ${extendedData.candles.length} candles for ${symbol}`);
        }
        return this.processExtendedData(extendedData, symbol, interval);
      }

      // If not enough data, try multiple periods
      console.warn('Trying to load more historical data...');
      return this.fetchMaxHistoricalData(symbol, interval, maxDataPoints);
      
    } catch (error) {
      // If rate limited, surface immediately and skip fallback loops
      if (error && error.status === 429) {
        console.warn('Rate limited while fetching extended data');
        throw error;
      }
      console.error('Error fetching extended data:', error);
      return this.fetchMaxHistoricalData(symbol, interval, maxDataPoints);
    }
  }

  // New method to fetch maximum historical data
  async fetchMaxHistoricalData(symbol, interval, maxDataPoints) {
    const allCandles = [];
    const periods = ['max', '2y', '1y', '6mo', '3mo'];
    
    for (const period of periods) {
      try {
        const { fetchMarketData } = await import('./api');
        const data = await fetchMarketData(symbol, interval, period);
        
        if (data && data.candles && data.candles.length > 0) {
          allCandles.push(...data.candles);
          if (DEBUG) {
            console.log(`Loaded ${data.candles.length} candles from period: ${period}`);
          }
        }
      } catch (error) {
        if (error && error.status === 429) {
          console.warn('Rate limited while fetching historical data; stopping retries');
          break;
        }
        console.warn(`Failed to fetch data with period ${period}:`, error);
      }
    }
    
    // Remove duplicates and sort
    const uniqueCandles = this.removeDuplicateCandles(allCandles);
    const sortedCandles = uniqueCandles.sort((a, b) => {
      const timeA = new Date(a.timestamp || a.time).getTime();
      const timeB = new Date(b.timestamp || b.time).getTime();
      return timeA - timeB;
    });

    const limitedCandles = maxDataPoints
      ? sortedCandles.slice(Math.max(0, sortedCandles.length - maxDataPoints))
      : sortedCandles;
    
    if (DEBUG) {
      console.log(`📊 Total unique candles loaded: ${sortedCandles.length}`);
    }
    
    return {
      candles: limitedCandles,
      symbol,
      interval,
      dataCount: limitedCandles.length
    };
  }

  // Process and optimize extended data
  processExtendedData(data, symbol, interval) {
    if (!data.candles || !Array.isArray(data.candles)) {
      return data;
    }

    // Sort data by timestamp
    const sortedCandles = data.candles.sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp) : new Date(a.time);
      const timeB = b.timestamp ? new Date(b.timestamp) : new Date(b.time);
      return timeA - timeB;
    });

    // Remove duplicates based on timestamp
    const uniqueCandles = this.removeDuplicateCandles(sortedCandles);

    // Validate and clean data
    const cleanedCandles = this.validateAndCleanCandles(uniqueCandles);

    return {
      ...data,
      candles: cleanedCandles,
      symbol,
      interval,
      dataCount: cleanedCandles.length,
      dateRange: {
        start: cleanedCandles[0]?.timestamp || cleanedCandles[0]?.time,
        end: cleanedCandles[cleanedCandles.length - 1]?.timestamp || cleanedCandles[cleanedCandles.length - 1]?.time
      }
    };
  }

  // Remove duplicate candles
  removeDuplicateCandles(candles) {
    const seen = new Set();
    return candles.filter(candle => {
      const timestamp = candle.timestamp || candle.time;
      const key = new Date(timestamp).getTime();
      
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  // Validate and clean candle data
  validateAndCleanCandles(candles) {
    return candles.filter(candle => {
      // Check required fields
      if (!candle || typeof candle !== 'object') return false;
      
      const requiredFields = ['open', 'high', 'low', 'close'];
      for (const field of requiredFields) {
        if (candle[field] === undefined || candle[field] === null || isNaN(candle[field])) {
          return false;
        }
      }

      // Validate price relationships
      const { open, high, low, close } = candle;
      if (high < Math.max(open, close) || low > Math.min(open, close)) {
        return false;
      }

      // Check timestamp
      const timestamp = candle.timestamp || candle.time;
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        return false;
      }

      return true;
    });
  }

  // Fallback to standard API with multiple periods
  async fetchFallbackData(symbol, interval) {
    const periods = ['max', '2y', '1y', '6mo', '3mo', '1mo'];
    
    for (const period of periods) {
      try {
        const { fetchMarketData } = await import('./api');
        const data = await fetchMarketData(symbol, interval, period);
        
        if (data && data.candles && data.candles.length > 100) {
          return this.processExtendedData(data, symbol, interval);
        }
      } catch (error) {
        console.warn(`Failed to fetch data with period ${period}:`, error);
        continue;
      }
    }

    throw new Error('Unable to fetch sufficient historical data');
  }

  // Load additional historical data (for progressive loading)
  async loadMoreHistoricalData(symbol, interval, beforeTimestamp, limit = 1000) {
    try {
      const endTime = new Date(beforeTimestamp).getTime();
      const startTime = this.calculateStartTime(interval, endTime, limit);
      
      const data = await fetchMarketDataRange(symbol, interval, startTime, endTime);
      
      if (data && data.candles) {
        return this.processExtendedData(data, symbol, interval);
      }
      
      return null;
    } catch (error) {
      console.error('Error loading more historical data:', error);
      return null;
    }
  }

  // Calculate start time based on interval and number of points
  calculateStartTime(interval, endTime, pointCount) {
    const intervalMs = this.getIntervalMilliseconds(interval);
    return endTime - (intervalMs * pointCount);
  }

  // Get milliseconds for an interval
  getIntervalMilliseconds(interval) {
    const intervals = {
      '1m': 60 * 1000,
      '5m': 5 * 60 * 1000,
      '15m': 15 * 60 * 1000,
      '30m': 30 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '4h': 4 * 60 * 60 * 1000,
      '1d': 24 * 60 * 60 * 1000,
      '1w': 7 * 24 * 60 * 60 * 1000,
    };
    
    return intervals[interval] || intervals['1d'];
  }

  // Clear cache for a specific symbol/interval
  clearCache(symbol, interval) {
    if (symbol && interval) {
      const key = this.getCacheKey(symbol, interval);
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Get cache statistics
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      keys: Array.from(this.cache.keys()),
      totalDataPoints: Array.from(this.cache.values()).reduce((total, cached) => {
        return total + (cached.data?.candles?.length || 0);
      }, 0)
    };
  }
}

// Create singleton instance
const dataManager = new DataManager();

export default dataManager;

// Export helper functions
export const loadExtendedMarketData = (symbol, interval, options) => 
  dataManager.loadExtendedData(symbol, interval, options);

export const loadMoreHistoricalData = (symbol, interval, beforeTimestamp, limit) => 
  dataManager.loadMoreHistoricalData(symbol, interval, beforeTimestamp, limit);

export const clearDataCache = (symbol, interval) => 
  dataManager.clearCache(symbol, interval);

export const getDataCacheStats = () => 
  dataManager.getCacheStats();
